import { LitElement, html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { BreakdownStateController } from '../../services/breakdown-state.js';
import type { BreakdownHierarchyNode, BreakdownLevel, BreakdownStatus } from '../../services/breakdown-api.js';
import './cp-goal-card.js';
import '../shared/cp-confirm-dialog.js';

const LEVEL_CONFIG: Record<BreakdownLevel, { label: string; order: number }> = {
  monthly: { label: 'Monatsziele', order: 0 },
  weekly: { label: 'Wochenziele', order: 1 },
  daily: { label: 'Tagesziele', order: 2 },
};

const MONTH_NAMES = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

function getCurrentPeriodLabel(level: BreakdownLevel): string {
  const now = new Date();
  if (level === 'monthly') {
    return `${MONTH_NAMES[now.getMonth()]} ${now.getFullYear()}`;
  }
  if (level === 'weekly') {
    const oneJan = new Date(now.getFullYear(), 0, 1);
    const days = Math.floor((now.getTime() - oneJan.getTime()) / 86400000);
    const week = Math.ceil((days + oneJan.getDay() + 1) / 7);
    return `KW ${String(week).padStart(2, '0')}`;
  }
  return `${String(now.getDate()).padStart(2, '0')}.${String(now.getMonth() + 1).padStart(2, '0')}.${now.getFullYear()}`;
}

@customElement('cp-goal-hierarchy')
export class CpGoalHierarchy extends LitElement {
  createRenderRoot() { return this; }

  @state() private _expandedGoalId: number | null = null;
  @state() private _showDeleteConfirm = false;

  private _breakdown = new BreakdownStateController(this);

  connectedCallback() {
    super.connectedCallback();
    if (this._breakdown.hierarchyLoadingState === 'idle') {
      void this._breakdown.loadHierarchy();
    }
  }

  private _toggleGoal(goalId: number): void {
    this._expandedGoalId = this._expandedGoalId === goalId ? null : goalId;
  }

  private _onEditClick(e: CustomEvent<{ goalId: number }>): void {
    this.dispatchEvent(
      new CustomEvent('goal-edit', { detail: e.detail, bubbles: true, composed: true }),
    );
  }

  private _onStatusChange(e: CustomEvent<{ goalId: number; status: BreakdownStatus }>): void {
    void this._breakdown.updateGoalStatus(e.detail.goalId, e.detail.status);
  }

  private _getGoalsByLevel(level: BreakdownLevel): BreakdownHierarchyNode[] {
    return this._breakdown.hierarchy.filter(n => n.level === level);
  }

  private _getLevelStats(goals: BreakdownHierarchyNode[]): { total: number; done: number; percent: number } {
    const total = goals.length;
    const done = goals.filter(g => g.status === 'done').length;
    const percent = total > 0 ? Math.round((done / total) * 100) : 0;
    return { total, done, percent };
  }

  private _getAllChildrenFlat(nodes: BreakdownHierarchyNode[], level: BreakdownLevel): BreakdownHierarchyNode[] {
    const result: BreakdownHierarchyNode[] = [];
    const collect = (items: BreakdownHierarchyNode[]): void => {
      for (const item of items) {
        if (item.level === level) {
          result.push(item);
        }
        if (item.children.length > 0) {
          collect(item.children);
        }
      }
    };
    collect(nodes);
    return result;
  }

  private _countAllNodes(nodes: BreakdownHierarchyNode[]): number {
    let count = 0;
    for (const node of nodes) {
      count += 1 + this._countAllNodes(node.children);
    }
    return count;
  }

  private _renderProgressBar(stats: { total: number; done: number; percent: number }) {
    return html`
      <div class="cp-goal-hierarchy__progress">
        <div class="cp-goal-hierarchy__progress-bar">
          <div
            class="cp-goal-hierarchy__progress-fill"
            style="width: ${stats.percent}%"
          ></div>
        </div>
        <span class="cp-goal-hierarchy__progress-text">${stats.done} von ${stats.total} erledigt (${stats.percent}%)</span>
      </div>
    `;
  }

  private _renderEmptyState() {
    return html`
      <div class="cp-goal-hierarchy__empty">
        <p class="cp-goal-hierarchy__empty-text">Noch keine Ziele vorhanden</p>
        <button
          class="cp-goal-hierarchy__empty-btn"
          @click=${this._onStartWorkflow}
        >Monthly Breakdown starten</button>
      </div>
    `;
  }

  private _requestDeleteAll(): void {
    this._showDeleteConfirm = true;
  }

  private _cancelDelete(): void {
    this._showDeleteConfirm = false;
  }

  private async _confirmDeleteAll(): Promise<void> {
    this._showDeleteConfirm = false;
    await this._breakdown.deleteAllGoals();
  }

  private _onStartWorkflow(): void {
    this.dispatchEvent(
      new CustomEvent('start-workflow', { bubbles: true, composed: true }),
    );
  }

  private _renderLevel(level: BreakdownLevel) {
    const config = LEVEL_CONFIG[level];
    let goals: BreakdownHierarchyNode[];

    if (level === 'monthly') {
      goals = this._getGoalsByLevel('monthly');
    } else {
      // Only show orphan goals (root-level) for weekly/daily — nested ones are shown under their parent
      goals = this._getGoalsByLevel(level);
    }

    if (goals.length === 0) return nothing;

    const stats = this._getLevelStats(goals);

    return html`
      <section class="cp-goal-hierarchy__level">
        <div class="cp-goal-hierarchy__level-header">
          <h3 class="cp-goal-hierarchy__level-title">${level !== 'monthly' ? 'Weitere ' : ''}${config.label} <span class="cp-goal-hierarchy__period">${getCurrentPeriodLabel(level)}</span></h3>
          <span class="cp-goal-hierarchy__level-count">${goals.length}</span>
        </div>
        ${this._renderProgressBar(stats)}
        <div class="cp-goal-hierarchy__level-goals">
          ${goals.map(goal => this._renderGoalWithChildren(goal))}
        </div>
      </section>
    `;
  }

  private _renderGoalWithChildren(goal: BreakdownHierarchyNode): TemplateResult {
    const hasChildren = goal.children.length > 0;
    const isExpanded = this._expandedGoalId === goal.id;

    return html`
      <div class="cp-goal-hierarchy__goal-group">
        <cp-goal-card
          .goal=${goal}
          .expandable=${hasChildren}
          .expanded=${isExpanded}
          @goal-click=${(e: CustomEvent<{ goalId: number }>) => this._toggleGoal(e.detail.goalId)}
          @edit-click=${this._onEditClick}
          @status-change=${this._onStatusChange}
        ></cp-goal-card>
        ${hasChildren && (goal.level === 'monthly' || isExpanded) ? html`
          <div class="cp-goal-hierarchy__children">
            ${goal.level === 'monthly' ? html`
              <span class="cp-goal-hierarchy__children-label">Wochenziele · ${getCurrentPeriodLabel('weekly')}</span>
            ` : nothing}
            ${goal.children.map(child => html`
              <div class="cp-goal-hierarchy__child-item">
                ${child.children.length === 0 ? html`
                  <cp-goal-card
                    .goal=${child}
                    .expandable=${false}
                    is-child
                    @edit-click=${this._onEditClick}
                    @status-change=${this._onStatusChange}
                  ></cp-goal-card>
                ` : html`
                  ${this._renderGoalWithChildren(child)}
                `}
              </div>
            `)}
          </div>
        ` : nothing}
        ${!hasChildren && goal.level === 'monthly' ? html`
          <div class="cp-goal-hierarchy__no-children">
            <span class="cp-goal-hierarchy__no-children-text">Keine Wochenziele - Weekly Breakdown starten</span>
          </div>
        ` : nothing}
      </div>
    `;
  }

  render() {
    if (this._breakdown.hierarchyLoadingState === 'loading') {
      return html`<div class="cp-goal-hierarchy"><p class="cp-goal-hierarchy__loading">Laden...</p></div>`;
    }

    if (this._breakdown.hierarchy.length === 0) {
      return html`<div class="cp-goal-hierarchy">${this._renderEmptyState()}</div>`;
    }

    const monthlyGoals = this._getGoalsByLevel('monthly');
    const weeklyGoals = this._getGoalsByLevel('weekly');
    const dailyGoals = this._getGoalsByLevel('daily');

    const totalGoals = this._countAllNodes(this._breakdown.hierarchy);

    return html`
      <div class="cp-goal-hierarchy">
        ${monthlyGoals.length > 0 ? this._renderLevel('monthly') : nothing}
        ${weeklyGoals.length > 0 ? this._renderLevel('weekly') : nothing}
        ${dailyGoals.length > 0 ? this._renderLevel('daily') : nothing}

        ${monthlyGoals.length === 0 ? this._renderEmptyState() : nothing}

        ${totalGoals > 0 ? html`
          <div class="cp-goal-hierarchy__actions">
            <button
              class="cp-goal-hierarchy__delete-all-btn"
              @click=${this._requestDeleteAll}
            >Alle Ziele loeschen</button>
          </div>
        ` : nothing}
      </div>
      <cp-confirm-dialog
        .open=${this._showDeleteConfirm}
        title="Alle Ziele loeschen"
        message="Alle ${totalGoals} Ziele (Monats-, Wochen- und Tagesziele) werden unwiderruflich geloescht. Fortfahren?"
        confirmLabel="Alle loeschen"
        variant="danger"
        @dialog-confirmed=${this._confirmDeleteAll}
        @dialog-cancelled=${this._cancelDelete}
      ></cp-confirm-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-goal-hierarchy': CpGoalHierarchy;
  }
}
